import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatCurrency, formatDateTime, StatusPill } from "@/lib/utils-format";
import { CaretLeft, UploadSimple, Paperclip, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fileUrl } from "@/components/FileUploader";

export default function CustomerOrderDetails() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [proof, setProof] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/customer/orders/${id}`);
      setData(data);
    } catch {
      toast.error("Could not load the order");
    }
  };
  useEffect(() => { load(); }, [id]);

  const uploadProof = async () => {
    if (!proof) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", proof);
    try {
      await api.post(`/customer/orders/${id}/payment-proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Payment proof uploaded");
      setProof(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!data) return <p className="text-sm text-zinc-500">Loading…</p>;
  const { order, attachments } = data;

  return (
    <div data-testid="customer-order-details">
      <Link to="/customer" className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-4">
        <CaretLeft size={14} /> Back to My Orders
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Order</p>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-black tracking-tighter font-mono">#{order.order_number}</h1>
            <StatusPill status={order.status} />
          </div>
          <p className="text-sm text-zinc-500 mt-1">Placed {formatDateTime(order.created_at)}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Total</p>
          <p className="text-2xl font-black font-mono">{formatCurrency(order.total_amount, order.currency)}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="border border-zinc-200 bg-white p-6">
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Delivery</p>
          <Row label="Address" value={order.address} />
          <Row label="Country" value={order.country} />
          {order.tracking_number && (
            <>
              <Row label="Tracking #" value={order.tracking_number} mono />
              <Row label="Courier" value={order.courier} />
            </>
          )}
        </div>
        <div className="border border-zinc-200 bg-white p-6">
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">Order</p>
          <Row label="Product" value={order.product} />
          <Row label="Quantity" value={order.quantity} />
          <Row label="Payment method" value={order.payment_method} />
          <Row label="Payment reference" value={order.payment_reference || "—"} />
          <Row label="Payment verified" value={order.payment_verified ? "Yes" : "Awaiting verification"} />
        </div>
      </div>

      <div className="border border-zinc-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Paperclip size={16} weight="bold" />
          <h3 className="text-sm font-bold uppercase tracking-widest">Payment proofs</h3>
        </div>
        <div className="flex items-end gap-3 mb-4">
          <div className="flex-1">
            <Input type="file" accept="image/*,application/pdf" onChange={(e) => setProof(e.target.files[0])} data-testid="customer-proof-input" />
            {proof && <p className="mt-1 text-xs text-zinc-500">Selected: {proof.name}</p>}
          </div>
          <Button onClick={uploadProof} disabled={!proof || uploading} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="customer-proof-upload">
            {uploading ? <CircleNotch size={14} className="animate-spin" /> : <><UploadSimple size={14} weight="bold" className="mr-1" /> Upload</>}
          </Button>
        </div>
        {attachments.length === 0 ? (
          <p className="text-sm text-zinc-500">No files uploaded yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="customer-attachments-list">
            {attachments.map((a) => {
              const src = a.download_url ? fileUrl(a.download_url) : a.data_url;
              const dl = a.download_url ? fileUrl(a.download_url, { inline: false }) : a.data_url;
              const isImage = (a.content_type || "").startsWith("image/");
              return (
                <div key={a.attachment_id} className="border border-zinc-200 p-3" data-testid={`customer-att-${a.attachment_id}`}>
                  {isImage ? (
                    <a href={dl} target="_blank" rel="noreferrer">
                      <img src={src} alt={a.filename} className="w-full h-32 object-cover border border-zinc-100" />
                    </a>
                  ) : (
                    <a href={dl} className="w-full h-32 flex items-center justify-center bg-zinc-100 border border-zinc-200 text-sm text-zinc-700 hover:underline">
                      Download PDF
                    </a>
                  )}
                  <p className="text-xs mt-2 truncate">{a.filename}</p>
                  <p className="text-[10px] text-zinc-500 font-mono">{formatDateTime(a.created_at)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-zinc-100 last:border-0">
      <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</span>
      <span className={`text-sm font-medium text-right ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}
